import { KeyboardEventTypes, Vector3 } from '@babylonjs/core';
import { Button, Control, InputText, StackPanel, TextBlock } from '@babylonjs/gui';
import { AssetFactory, ManifestEntry } from './AssetFactory';
import manifest from './assets/manifest.json';
import { Game } from './Game';
import { GameState } from './GameState';
import { Gui } from './gui/Gui';
import { LevelConfigLoader } from './LevelConfigLoader';
import { MatchmakingService } from './MatchmakingService';

/**
 * Multiplayer Lobby — Layout:
 *
 *  ┌──────────────────┐  ┌──────────────────┐
 *  │   👑 Host Game   │  │   🔗 Join Game   │
 *  │                  │  │                  │
 *  │   Room Code:     │  │  [Enter Code___] │
 *  │     BAFIK3       │  │                  │
 *  │                  │  │  🔍 Open Games   │
 *  │  📋 Copy  📤 Share│  │  ┌──────────────┐│
 *  │                  │  │  │ Host1 [ABC]  ││
 *  │                  │  │  │ Host2 [XYZ]  ││
 *  └──────────────────┘  │  └──────────────┘│
 *                        └──────────────────┘
 *          ┌──────────────────┐
 *          │  🎮 Start Game   │
 *          └──────────────────┘
 *          Status text here...
 */
export class Lobby {
  private statusText: TextBlock;
  private peerIdText: TextBlock;
  private joinInput: InputText;
  private startButton: Button;
  private copyLinkButton: Button;
  private shareButton: Button;
  private roomListPanel: StackPanel;
  private levelListPanel: StackPanel;
  private selectedLevelKey: string = 'test';
  private levelButtons: { key: string; button: Button }[] = [];
  private refreshInterval: ReturnType<typeof setInterval> | null = null;

  constructor(public game: Game) {}

  async create() {
    await Gui.createDefaultScene(
      this.game,
      {
        url: 'assets/skybox/skybox_ruins.glb',
        rotation: new Vector3(-Math.PI / 2, 0, 0),
        rotationAxis: 'y',
      },
      'Lobby'
    );

    await Gui.createBackButton(this.game, GameState.MAIN, '🔙');
    await this.game.sound.createBackgroundMusic('erokia');

    this.createHostSection();
    this.createJoinSection();
    this.createStatusSection();

    // Store callbacks for removal on dispose
    const onStartLevel = (payload: { levelKey: string; seed: number }) => {
      console.log('[Lobby] Received START_LEVEL', payload);
      this.game.config.levelKey = payload.levelKey;
      this.game.config.seed = payload.seed;
      this.cleanup();
      this.game.routeTo(GameState.LEVEL);
    };

    const onPlayerDisconnected = () => {
      this.setStatus('Peer disconnected.');
      if (this.startButton) {
        this.startButton.isVisible = false;
      }
    };

    // Listen for network events
    this.game.network.onMessage('START_LEVEL', onStartLevel);
    this.game.network.onMessage('PLAYER_DISCONNECTED', onPlayerDisconnected);

    // Ensure cleanup when UI is disposed
    this.game.gui2D.ui.rootContainer.onDisposeObservable.addOnce(() => {
      this.cleanup();
      this.game.network.removeMessageHandler('START_LEVEL', onStartLevel);
      this.game.network.removeMessageHandler('PLAYER_DISCONNECTED', onPlayerDisconnected);
    });

    this.setupInput();

    // Auto-join if we arrived via ?join=ROOMCODE
    if (this.game.config.joinCode) {
      const code = this.game.config.joinCode;
      this.game.config.joinCode = undefined;
      this.joinInput.text = code;
      setTimeout(() => this.joinWithCode(code), 500);
    }
  }

  private setupInput() {
    this.game.removeableObservers.push(
      this.game.scene.onKeyboardObservable.add((kbInfo) => {
        if (kbInfo.type === KeyboardEventTypes.KEYUP) {
          if (kbInfo.event.code === 'Escape') {
            this.back();
          }
        }
      })
    );
  }

  private back() {
    this.game.sound.play('click');
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.game.network.isConnected()) {
      this.game.network.disconnect();
    }
    if (this.game.network.roomCode) {
      MatchmakingService.unregisterRoom(this.game.network.roomCode);
    }
    this.game.routeTo(GameState.MAIN);
  }

  private setStatus(text: string) {
    if (this.statusText) {
      this.statusText.text = text;
    }
  }

  private getJoinLink(code: string): string {
    const url = new URL(window.location.href);
    url.searchParams.set('join', code);
    return url.toString();
  }

  // ─── Left Column: Host ────────────────────────────────────────────────────

  private createHostSection() {
    const root = this.game.gui2D.ui.rootContainer;
    const colors = this.game.gui2D.sceneButtonColors();

    // Host button — top-left (mirrors Join Game on the right)
    // Level selection panel created after (below the button)

    // (Level selection panel is created at the end of this method, positioned below)
    const hostButton = this.game.gui2D.createButton(
      'button_host',
      '👑 Host Game',
      async () => {
        this.game.sound.play('click');
        this.setStatus('Creating room...');
        try {
          const roomCode = await this.game.network.hostGame();
          this.peerIdText.text = `Room Code: ${roomCode}`;
          this.copyLinkButton.isVisible = true;
          if (navigator.share) {
            this.shareButton.isVisible = true;
          }

          await MatchmakingService.registerRoom(roomCode, roomCode, this.game.user.name);
          this.refreshRoomList();
          this.setStatus('Waiting for another player to join...');

          const checkConn = setInterval(() => {
            if (this.game.network.isConnected()) {
              clearInterval(checkConn);
              this.setStatus('Player joined! You can start the game.');
              this.startButton.isVisible = true;
            }
          }, 500);
        } catch (e) {
          this.setStatus('Error creating room.');
        }
      },
      root,
      1,
      colors
    );
    this.game.gui2D.styleSceneButton(hostButton);
    hostButton.width = '22%';
    hostButton.left = '-20%';
    hostButton.top = '-15%';

    // Level selection panel — below host button (mirrors Open Games on the right)
    this.createLevelSelectionPanel();

    // Room code display — compact single line, between button and panel
    this.peerIdText = this.game.gui2D.createTextBlock('text_peerid', '', root, Gui.fontSizeS);
    this.peerIdText.left = '-20%';
    this.peerIdText.top = '-8%';
    this.peerIdText.height = '20px';
    this.peerIdText.color = 'rgba(255,255,255,0.7)';
    this.peerIdText.isHitTestVisible = false;
    this.peerIdText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    // Copy Link button — below room code
    this.copyLinkButton = this.game.gui2D.createButton(
      'button_copy_link',
      '📋 Copy Link',
      () => {
        this.game.sound.play('click');
        if (this.game.network.roomCode) {
          const link = this.getJoinLink(this.game.network.roomCode);
          navigator.clipboard.writeText(link);
          this.setStatus('Join link copied!');
        }
      },
      root,
      1,
      colors
    );
    this.game.gui2D.styleSceneButton(this.copyLinkButton);
    this.copyLinkButton.width = '10%';
    this.copyLinkButton.height = '5%';
    this.copyLinkButton.fontSize = Gui.fontSizeXS;
    this.copyLinkButton.left = '-26%';
    this.copyLinkButton.top = '-3%';
    this.copyLinkButton.isVisible = false;

    // Share button — next to copy link
    this.shareButton = this.game.gui2D.createButton(
      'button_share',
      '📤 Share',
      async () => {
        this.game.sound.play('click');
        if (this.game.network.roomCode) {
          try {
            await navigator.share({
              title: 'Join my Platformia game!',
              text: `Join my game with code: ${this.game.network.roomCode}`,
              url: this.getJoinLink(this.game.network.roomCode),
            });
          } catch (e) {
            /* cancelled */
          }
        }
      },
      root,
      1,
      colors
    );
    this.game.gui2D.styleSceneButton(this.shareButton);
    this.shareButton.width = '10%';
    this.shareButton.height = '5%';
    this.shareButton.fontSize = Gui.fontSizeXS;
    this.shareButton.left = '-14%';
    this.shareButton.top = '-3%';
    this.shareButton.isVisible = false;
  }

  private async createLevelSelectionPanel() {
    const root = this.game.gui2D.ui.rootContainer;

    // Container panel — top-left
    const panel = this.game.gui2D.createRectangle('22%', '28%', root);
    panel.left = '-20%';
    panel.top = '15%';
    panel.thickness = 1;
    panel.background = Gui.controlBackground;
    panel.cornerRadius = 8;
    panel.color = 'rgba(255,255,255,0.2)';

    // Title
    const title = this.game.gui2D.createTextBlock('level_select_title', '🗺️ Select Level', panel, Gui.fontSizeL);
    title.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    title.top = '6px';
    title.height = '24px';
    title.color = 'rgba(255,255,255,0.7)';
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    // Scrollable level list
    this.levelListPanel = new StackPanel('level_list_panel');
    this.levelListPanel.isVertical = true;
    this.levelListPanel.width = '92%';
    this.levelListPanel.height = '80%';
    this.levelListPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.levelListPanel.top = '32px';
    panel.addControl(this.levelListPanel);

    // Loading placeholder
    const loadingText = this.game.gui2D.createTextBlock('level_loading', 'Loading...', this.levelListPanel, Gui.fontSizeL);
    loadingText.color = 'rgba(255,255,255,0.4)';
    loadingText.height = '28px';

    // Load levels async
    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    const names = await LevelConfigLoader.getLevelNames();

    // Clear placeholder
    this.levelListPanel.removeControl(loadingText);

    // Default selection: first real level or 'test'
    const firstKey = levels.length > 0 ? levels[0].name.replace('.json', '') : 'test';
    this.selectedLevelKey = firstKey;
    this.game.config.levelKey = firstKey;

    // Add a button per level
    for (let i = 0; i < levels.length; i++) {
      const key = levels[i].name.replace('.json', '');
      const name = names[i] || key;
      this.addLevelButton(key, name);
    }

    // Highlight the default selection
    this.updateLevelSelection(this.selectedLevelKey);
  }

  private addLevelButton(key: string, name: string) {
    const btn = this.game.gui2D.createButton(
      `level_btn_${key}`,
      name,
      () => {
        this.game.sound.play('click');
        this.selectedLevelKey = key;
        this.game.config.levelKey = key;
        this.updateLevelSelection(key);
      },
      this.levelListPanel,
      1,
      this.game.gui2D.sceneButtonColors()
    );
    btn.width = '100%';
    btn.height = '34px';
    btn.fontSize = Gui.fontSizeL;
    btn.thickness = 0;
    btn.cornerRadius = 4;
    btn.paddingTop = '2px';
    btn.paddingBottom = '2px';
    this.levelButtons.push({ key, button: btn });
  }

  private updateLevelSelection(selectedKey: string) {
    for (const { key, button } of this.levelButtons) {
      if (key === selectedKey) {
        button.background = 'rgba(255,220,50,0.25)';
        button.color = '#ffd700';
        (button.children[0] as TextBlock).color = '#ffd700';
      } else {
        button.background = 'transparent';
        button.color = 'rgba(255,255,255,0.7)';
        (button.children[0] as TextBlock).color = 'rgba(255,255,255,0.7)';
      }
    }
  }

  // ─── Right Column: Join + Open Games ──────────────────────────────────────

  private createJoinSection() {
    const root = this.game.gui2D.ui.rootContainer;
    const colors = this.game.gui2D.sceneButtonColors();

    // Join button — top-right area
    const joinButton = this.game.gui2D.createButton(
      'button_join',
      '🔗 Join Game',
      async () => {
        this.game.sound.play('click');
        const code = this.joinInput.text.trim().toUpperCase();
        if (!code) {
          this.setStatus('Please enter a Room Code.');
          return;
        }
        await this.joinWithCode(code);
      },
      root,
      1,
      colors
    );
    this.game.gui2D.styleSceneButton(joinButton);
    joinButton.width = '22%';
    joinButton.left = '20%';
    joinButton.top = '-15%';

    // Code input field — below join button
    this.joinInput = this.game.gui2D.createTextField('input_join', '', () => {}, root, 'Room Code');
    this.joinInput.isPointerBlocker = true;
    this.joinInput.fontSize = Gui.fontSizeM;
    this.joinInput.width = '22%';
    this.joinInput.height = Gui.fontSizeXL;
    this.joinInput.color = 'white';
    this.joinInput.left = '20%';
    this.joinInput.top = '-3%';
    this.joinInput.thickness = 1;
    this.joinInput.background = Gui.controlBackground;

    // Open Games panel — below input
    this.createOpenGamesPanel();
  }

  private createOpenGamesPanel() {
    const root = this.game.gui2D.ui.rootContainer;
    const colors = this.game.gui2D.sceneButtonColors();

    // Panel container
    const panel = this.game.gui2D.createRectangle('22%', '28%', root);
    panel.left = '20%';
    panel.top = '15%';
    panel.thickness = 1;
    panel.background = Gui.controlBackground;
    panel.cornerRadius = 8;
    panel.color = 'rgba(255,255,255,0.2)';

    // Title bar
    const title = this.game.gui2D.createTextBlock('matchmaking_title', '🔍 Open Games', panel, Gui.fontSizeL);
    title.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    title.top = '6px';
    title.height = '24px';
    title.color = 'rgba(255,255,255,0.7)';
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    // Refresh button in title bar
    const refreshButton = this.game.gui2D.createButton(
      'button_refresh',
      '🔄',
      () => {
        this.game.sound.play('click');
        this.refreshRoomList();
      },
      panel,
      1,
      colors
    );
    refreshButton.width = '26px';
    refreshButton.height = '24px';
    refreshButton.fontSize = '14px';
    refreshButton.thickness = 0;
    refreshButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    refreshButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    refreshButton.top = '4px';
    refreshButton.left = '-6px';

    // Room list stack
    this.roomListPanel = new StackPanel('room_list_panel');
    this.roomListPanel.isVertical = true;
    this.roomListPanel.width = '92%';
    this.roomListPanel.height = '80%';
    this.roomListPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.roomListPanel.top = '32px';
    panel.addControl(this.roomListPanel);

    // Initial fetch + auto-refresh
    this.refreshRoomList();
    this.refreshInterval = setInterval(() => this.refreshRoomList(), 5000);
  }

  private async refreshRoomList() {
    if (!this.roomListPanel || (this.roomListPanel as any).isDisposed) {
      this.cleanup();
      return;
    }

    const rooms = await MatchmakingService.listRooms();

    if (!this.roomListPanel || (this.roomListPanel as any).isDisposed) {
      return;
    }

    // Clear existing entries
    const children = this.roomListPanel.children.slice();
    children.forEach((c) => this.roomListPanel.removeControl(c));

    if (rooms.length === 0) {
      const emptyText = this.game.gui2D.createTextBlock('no_rooms', 'No open games.', this.roomListPanel, Gui.fontSizeL);
      emptyText.color = 'rgba(255,255,255,0.4)';
      emptyText.height = '28px';
      emptyText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      return;
    }

    for (const room of rooms) {
      const roomButton = this.game.gui2D.createButton(
        `room_${room.code}`,
        `🎮 ${room.hostName}  ·  ${room.code}`,
        async () => {
          this.game.sound.play('click');
          await this.joinWithCode(room.code);
        },
        this.roomListPanel,
        1,
        this.game.gui2D.sceneButtonColors()
      );
      roomButton.width = '100%';
      roomButton.height = '34px';
      roomButton.fontSize = Gui.fontSizeL;
      roomButton.thickness = 0;
      roomButton.cornerRadius = 4;
      roomButton.paddingTop = '2px';
      roomButton.paddingBottom = '2px';
    }
  }

  // ─── Join Logic ───────────────────────────────────────────────────────────

  private async joinWithCode(code: string) {
    this.setStatus(`Looking up room ${code}...`);
    try {
      const peerId = await MatchmakingService.lookupRoom(code);
      const connectId = peerId || code;

      this.setStatus(`Connecting to ${code}...`);
      await this.game.network.joinGame(connectId);
      this.setStatus('Connected! Waiting for host to start...');
    } catch (e) {
      this.setStatus('Error joining game. Check the room code.');
    }
  }

  // ─── Bottom Center: Start + Status ────────────────────────────────────────

  private createStatusSection() {
    const root = this.game.gui2D.ui.rootContainer;
    const colors = this.game.gui2D.sceneButtonColors();

    // Start Game button — bottom center
    this.startButton = this.game.gui2D.createButton(
      'button_start',
      '🎮 Start',
      () => {
        this.game.sound.play('click');
        if (this.game.network.isConnected()) {
          const randomSeed = Math.floor(Math.random() * 1000000);
          this.game.config.seed = randomSeed;

          this.game.network.sendMessage('START_LEVEL', {
            levelKey: this.game.config.levelKey,
            seed: randomSeed,
          });

          if (this.game.network.roomCode) {
            MatchmakingService.unregisterRoom(this.game.network.roomCode);
          }

          if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
          }

          this.game.routeTo(GameState.LEVEL);
        }
      },
      root,
      1,
      colors
    );
    this.game.gui2D.styleSceneButton(this.startButton);
    this.startButton.width = '12%';
    this.startButton.left = '0%';
    this.startButton.top = '15%';
    this.startButton.isVisible = false;

    // Status text — below start button
    this.statusText = this.game.gui2D.createTextBlock('lobby_status', '', root, Gui.fontSizeS);
    this.statusText.top = '40%';
    this.statusText.left = '0%';
    this.statusText.color = 'white';
    this.statusText.isHitTestVisible = false;
    this.statusText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
  }

  private cleanup() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
}
